<template>
  <v-container grid-list fluid>
    <v-layout row wrap>
      <v-flex v-if="user.managerOrHigher" xs12 class="mb-2">
        <v-layout row v-if="shift.sunsetAfter" class="mb-2">
          <v-flex xs3>
            <v-datetime-picker
              readonly
              disabled
              v-model="shift.sunsetAfter"
              label="Sunset time (shift will be cancelled if not filled by this time)"
              format="ddd, MMM DD YYYY"
            >
            </v-datetime-picker>
          </v-flex>

          <v-flex xs8>
            {{
              this.$moment(newShiftStart).diff(
                this.$moment(shift.sunsetAfter),
                "days"
              )
            }}
            days from shift start
          </v-flex>
        </v-layout>

        <v-layout row>
          <v-flex class="mr-2">
            <date-picker
              v-model="newShiftStart"
              type="datetime"
              :time-picker-options="{
                start: '06:00',
                step: '00:15',
                end: '23:45'
              }"
              format="ddd, MMM DD YYYY, h:mma"
              placeholder="Shift Start"
            ></date-picker>
            <span
              v-if="startDateChanged"
              style="color: #19a119; font-size: 12px;"
              >Saved!</span
            >
          </v-flex>
          <v-flex>
            <date-picker
              v-model="newShiftEnd"
              type="datetime"
              :time-picker-options="{
                start: '06:00',
                step: '00:15',
                end: '23:45'
              }"
              format="ddd, MMM DD YYYY, h:mma"
              placeholder="Shift End"
            ></date-picker>
            <span v-if="endDateChanged" style="color: #19a119; font-size: 12px;"
              >Saved!</span
            >
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex class="mr-2">
            <ApiField
              prefix="$"
              label="Bonus"
              storeAction="shifts/update"
              storeUpdate="shifts/updateViewedShift"
              paramName="shift"
              :object="shift"
              fieldName="straight_bonus_amount"
              :storeFieldValue="
                shift.straightBonusAmount
                  ? shift.straightBonusAmount.replace(/\$/, '')
                  : ''
              "
            >
            </ApiField>
          </v-flex>
          <v-flex class="mr-2">
            <v-layout row>
              <v-flex>
                <ApiField
                  prefix="$"
                  :hint="shift.hasCoverBonus ? 'Not billed to client' : ''"
                  label="Call In Bonus"
                  storeAction="shifts/update"
                  storeUpdate="shifts/updateViewedShift"
                  paramName="shift"
                  :object="shift"
                  fieldName="call_in_bonus_amount"
                  :storeFieldValue="
                    shift.callInBonusAmount
                      ? shift.callInBonusAmount.replace(/\$/, '')
                      : ''
                  "
                >
                </ApiField>
              </v-flex>
              <v-flex shrink class="mr-2">
                <ApiField
                  fieldType="switch"
                  label="Cover Bonus"
                  storeAction="shifts/update"
                  storeUpdate="shifts/updateViewedShift"
                  paramName="shift"
                  :object="shift"
                  fieldName="has_cover_bonus"
                  :storeFieldValue="shift.hasCoverBonus"
                >
                </ApiField>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex>
            <ApiField
              prefix="$"
              label="Travel Bonus"
              storeAction="shifts/update"
              storeUpdate="shifts/updateViewedShift"
              paramName="shift"
              :object="shift"
              fieldName="travel_bonus_amount"
              :storeFieldValue="
                shift.travelBonusAmount
                  ? shift.travelBonusAmount.replace(/\$/, '')
                  : ''
              "
            >
            </ApiField>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex grow>
            <ApiField
              fieldType="autocomplete"
              :items="assignableEmployees"
              item-text="name"
              item-value="id"
              label="Employee"
              storeAction="shifts/update"
              storeUpdate="shifts/updateViewedShift"
              paramName="shift"
              :object="shift"
              fieldName="user_id"
              :storeFieldValue="shift.userId"
              clearable
            >
            </ApiField>
          </v-flex>
          <v-flex shrink>
            <v-btn
              :disabled="!shift.userId"
              flat
              icon
              color="primary"
              @click="messageEmployeeDialog = true"
            >
              <v-icon>mail_outline</v-icon>
            </v-btn>
            <v-dialog max-width="400" v-model="messageEmployeeDialog">
              <v-card>
                <v-card-text class="text-xs-center">
                  <h2 class="headline">Send messages?</h2>
                  <p>
                    This will send messages to the employee and client
                    indicating that this employee has picked up this shift.
                  </p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn flat @click="messageEmployeeDialog = false"
                    >Nevermind</v-btn
                  >
                  <v-btn color="success" @click="sendPickUpMessage"
                    >Send it!</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex>
            <ApiField
              fieldType="textarea"
              label="Details"
              storeAction="shifts/update"
              storeUpdate="shifts/updateViewedShift"
              paramName="shift"
              :object="shift"
              fieldName="details"
              :storeFieldValue="shift.details"
            >
            </ApiField>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex v-if="user.bohOrHigher">
        <v-layout row>
          <v-flex>
            <ApiField
              fieldType="date"
              label="Record creation date"
              storeAction="shifts/update"
              storeUpdate="shifts/updateViewedShift"
              paramName="shift"
              :object="shift"
              fieldName="created_at"
              :storeFieldValue="shift.createdAt"
              hint="Careful. Changing this field rewrites the history of when this shift was created."
            >
            </ApiField>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import ApiField from "@/components/base/api-field.vue";
import { mapState } from "vuex";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  props: ["shift", "user"],

  components: {
    ApiField,
    DatePicker
  },

  created() {
    this.setUp();
    this.newCreatedAtDate = this.shift.createdAt;
  },

  data() {
    return {
      newCreatedAtDate: "",
      createdDateMenu: false,

      messageEmployeeDialog: false,
      // sunsetDateMenu: false,
      endDateChanged: false,
      startDateChanged: false,
      userCursorIsHere: false,
      updateNGShift: this.shift.newGradAllowed,
      savingNG: false,
      skillOptions: [],
      skillsSelected: [],
      savingSkills: false,
      shiftDetailsRequest: this.shift.proposedDetails || this.shift.details,
      shiftStartRequest: this.shift.proposedStartDatetime
        ? this.$moment(this.shift.proposedStartDatetime).toDate()
        : this.$moment(this.shift.startDatetime).toDate(),
      shiftEndRequest: this.shift.proposedEndDatetime
        ? this.$moment(this.shift.proposedEndDatetime).toDate()
        : this.$moment(this.shift.endDatetime).toDate(),
      shiftBonusRequest:
        this.shift.proposedStraightBonusAmount > 0
          ? this.shift.proposedStraightBonusAmount
          : this.shift.straightBonusAmount.replace(/\$/, ""),
      updatingRequest: false,
      changeRequestApiCallPending: false
    };
  },

  computed: {
    ...mapState("serviceRequest", ["assignableEmployees"]),
    existingSkills() {
      return this.shift.requiredSkills.map(s => s.id);
    },
    skillsChanged() {
      // Any difference between the starting skills and the selected checkboxes
      return _.xor(this.skillsSelected, this.existingSkills).length > 0;
    },

    shiftTooSoon() {
      if (
        this.$moment(this.shift.startDatetime).isBefore(
          this.$moment().add(72, "hours")
        )
      ) {
        return true;
      } else {
        return false;
      }
    },

    newShiftStart: {
      get: function() {
        return this.$moment(this.shift.startDatetime).toDate();
      },
      set: function(newValue) {
        if (!newValue) {
          return;
        }

        this.startDateChanged = true;

        this.$store.dispatch("shifts/update", {
          id: this.shift.id,
          shift: {
            start_datetime: newValue
          }
        });

        this.successClear = setInterval(() => {
          this.startDateChanged = false;
        }, 1500);
      }
    },

    newShiftEnd: {
      get: function() {
        return this.$moment(this.shift.endDatetime).toDate();
      },
      set: function(newValue) {
        if (!newValue) {
          return;
        }

        this.endDateChanged = true;

        this.$store.dispatch("shifts/update", {
          id: this.shift.id,
          shift: {
            end_datetime: newValue
          }
        });

        this.successClear = setInterval(() => {
          this.endDateChanged = false;
        }, 1500);
      }
    },

    changeAlreadyRequested() {
      if (this.shift.changesProposed) {
        return true;
      } else {
        return false;
      }
    },
    isNGShift() {
      return this.shift.newGradAllowed;
    },
    ngChanged() {
      return this.updateNGShift != this.isNGShift;
    }
  },

  methods: {
    setUp() {
      this.$store.dispatch("serviceRequest/getSkills").then(res => {
        this.skillOptions = res;
      });
      this.skillsSelected = this.shift.requiredSkills.map(s => s.id);
    },

    sendPickUpMessage() {
      shiftApi.sendPickUpNotification({ shiftId: this.shift.id }, res => {});
      this.messageEmployeeDialog = false;
      this.$store.dispatch(
        "global/showSnackbarSuccess",
        {
          message: "Pick Up messages sent!"
        },
        error => {
          this.$store.dispatch("global/showSnackbarError", {
            message: "Something went wrong sending those messages."
          });
        }
      );
    },

    saveNG() {
      this.savingNG = true;
      this.$store
        .dispatch("shifts/update", {
          id: this.shift.id,
          shift: {
            new_grads_allowed: this.updateNGShift
          }
        })
        .then(res => {
          const message = `New Grad settings saveed`;
          this.$store.dispatch(
            "global/showSnackbarSuccess",
            { message: message },
            { root: true }
          );
        })
        .catch(err => {
          const message = `Something went wrong updating the New Grad settings`;
          this.$store.dispatch(
            "global/showSnackbarError",
            { message: message },
            { root: true }
          );
        })
        .finally(res => {
          this.savingNG = false;
        });
    },

    saveSkills() {
      this.savingSkills = true;
      this.$store
        .dispatch("shifts/update", {
          id: this.shift.id,
          shift: {
            required_skill_ids: this.skillsSelected
          }
        })
        .then(res => {
          const message = `Required Skills updated`;
          this.$store.dispatch(
            "global/showSnackbarSuccess",
            { message: message },
            { root: true }
          );
        })
        .catch(err => {
          const message = `Something went wrong updating the skills`;
          this.$store.dispatch(
            "global/showSnackbarError",
            { message: message },
            { root: true }
          );
        })
        .finally(res => {
          this.savingSkills = false;
        });
    },

    requestChanges() {
      let startHoursDiff = this.$moment
        .duration(
          this.$moment(this.shiftStartRequest).diff(this.shift.startDatetime)
        )
        .asHours();

      let endHoursDiff = this.$moment
        .duration(
          this.$moment(this.shiftEndRequest).diff(this.shift.endDatetime)
        )
        .asHours();

      if (Math.abs(startHoursDiff) > 6 || Math.abs(endHoursDiff) > 6) {
        const message = `The shift time cannot change by more than 6 hours!`;
        this.$store.dispatch(
          "global/showSnackbarError",
          { message: message },
          { root: true }
        );
        return;
      }

      this.changeRequestApiCallPending = true;
      this.$store
        .dispatch("shifts/update", {
          id: this.shift.id,
          shift: {
            proposed_start_datetime: this.shiftStartRequest,
            proposed_end_datetime: this.shiftEndRequest,
            proposed_straight_bonus_amount: this.shiftBonusRequest,
            proposed_details: this.shiftDetailsRequest
          }
        })
        .then(res => {
          const message = `Changes requested! The employee has been notified`;
          this.$store.dispatch(
            "global/showSnackbarSuccess",
            { message: message },
            { root: true }
          );
        })
        .finally(res => {
          this.updatingRequest = false;
          this.changesProposed = true;
          this.changeRequestApiCallPending = false;
        });
    }
  }
};
</script>
